import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  ERROR_OCCURRED,
  OK,
  PRE_ACTIVATION_SECTION_01_BUSINESS,
  PRE_ACTIVATION_SECTION_02_CURRENT_ADS,
  PRE_ACTIVATION_SECTION_03_OBJECTIVES,
  PRE_ACTIVATION_SECTION_04_MEETING_ACCESS,
  PRE_ACTIVATION_SECTION_AGENCY_01_BASICS,
  PRE_ACTIVATION_SECTION_AGENCY_02_LIVE_EXERCISE,
  PRE_ACTIVATION_SECTION_AGENCY_03_OPERATIONS,
  PRE_ACTIVATION_SECTION_AGENCY_04_MEETING_ACCESS,
  PRE_ACTIVATION_SECTION_CONTACT,
  PRE_ACTIVATION_SUBMIT_BUTTON,
  PRE_ACTIVATION_SUBMIT_SUCCESS_TITLE,
  PRE_ACTIVATION_SUBMIT_SUCCESS_MESSAGE,
  PRE_ACTIVATION_CONFIDENTIALITY_NOTE,
} from "../../i18n/keysTranslations";
import { SimpleAlert } from "../../components/Alerts/Alerts";
import { savePreActivationFormRequest } from "../../services/preActivation";
import Navbar from "./components/Navbar";
import Card from "./components/Card";
import HeroSection from "./sections/HeroSection";
import ContactSection from "./sections/ContactSection";
import BusinessSection from "./sections/BusinessSection";
import CurrentAdsSection from "./sections/CurrentAdsSection";
import ObjectivesSection from "./sections/ObjectivesSection";
import MeetingAccessSection from "./sections/MeetingAccessSection";
import AgencyBasicsSection from "./sections/AgencyBasicsSection";
import AgencyLiveExerciseSection from "./sections/AgencyLiveExerciseSection";
import AgencyOperationsSection from "./sections/AgencyOperationsSection";
import AgencyMeetingAccessSection from "./sections/AgencyMeetingAccessSection";
import { PAPER_BACKGROUND_COLOR_SLIDE_MENU } from "../../utils/colors";
import { APP_SUPPORTED_LANGUAGES } from "../../utils/constants";
import { createPreActivationSchema } from "./preActivationSchema";
import Button from "../../components/Buttons/Button";
import useFocusErrorForm from "../../hooks/useFocusErrorForm";
import CircularProgress from "@mui/material/CircularProgress";
import i18next from "i18next";
import LanguageSwitcher from "../../components/Common/LanguageSwitcher";
import {
  buildFormValuesForOrganizationType,
  defaultFormValues,
  PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY,
  sanitizePreActivationSubmitValues,
} from "./preActivationFormValues";

const getNormalizedLanguage = (language = "") => {
  if (!language) {
    return null;
  }
  const shortLanguage = language.toLowerCase().split("-")[0];
  return APP_SUPPORTED_LANGUAGES.includes(shortLanguage) ? shortLanguage : null;
};

// eslint-disable-next-line react-refresh/only-export-components
export const getBrowserLanguage = () => {
  const browserLanguage =
    navigator.language || navigator.languages?.[0] || "en";
  return getNormalizedLanguage(browserLanguage) || "en";
};

const LANGUAGE_SWITCHER_STYLES = {
  position: "fixed",
  top: 40,
  right: 16,
  zIndex: 1300,
};

const LANGUAGE_SWITCHER_BUTTON_STYLES = {
  minWidth: "max-content",
  border: "none !important",
  boxShadow: "none !important",
  backgroundColor: "transparent !important",
  px: "0 !important",
  "&.MuiButtonBase-root.MuiButton-root:hover": {
    backgroundColor: "transparent !important",
    border: "none !important",
    boxShadow: "none !important",
    color: "inherit !important",
  },
};

const LANGUAGE_SWITCHER_MENU_STYLES = {
  "& .MuiPaper-root": {
    marginTop: "8px",
  },
};

const PreActivationPageShell = ({ children, readOnly }) => (
  <Box
    sx={{
      minHeight: "100vh",
      background: PAPER_BACKGROUND_COLOR_SLIDE_MENU,
      padding: "24px 16px 48px",
    }}
  >
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        width: "100%",
        maxWidth: readOnly ? "100%" : "640px",
        margin: "0 auto",
      }}
    >
      {children}
    </Container>
  </Box>
);

const OwnerBackBar = ({ backTo }) => {
  const navigate = useNavigate();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        marginBottom: "16px",
      }}
    >
      <IconButton
        aria-label="back"
        onClick={() => navigate(backTo)}
        edge="start"
        size="small"
        sx={{ color: "text.primary" }}
      >
        <ArrowBackIcon />
      </IconButton>
    </Box>
  );
};

const BrandSectionCards = ({ control, errors, readOnly }) => {
  const { t } = useTranslation();

  return (
    <>
      <Card section={t(PRE_ACTIVATION_SECTION_01_BUSINESS)}>
        <BusinessSection
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      </Card>

      <Card section={t(PRE_ACTIVATION_SECTION_02_CURRENT_ADS)}>
        <CurrentAdsSection
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      </Card>

      <Card section={t(PRE_ACTIVATION_SECTION_03_OBJECTIVES)}>
        <ObjectivesSection
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      </Card>

      <Card section={t(PRE_ACTIVATION_SECTION_04_MEETING_ACCESS)}>
        <MeetingAccessSection
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      </Card>
    </>
  );
};

const AgencySectionCards = ({ control, errors, readOnly }) => {
  const { t } = useTranslation();

  return (
    <>
      <Card section={t(PRE_ACTIVATION_SECTION_AGENCY_01_BASICS)}>
        <AgencyBasicsSection
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      </Card>

      <Card section={t(PRE_ACTIVATION_SECTION_AGENCY_02_LIVE_EXERCISE)}>
        <AgencyLiveExerciseSection
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      </Card>

      <Card section={t(PRE_ACTIVATION_SECTION_AGENCY_03_OPERATIONS)}>
        <AgencyOperationsSection
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      </Card>

      <Card section={t(PRE_ACTIVATION_SECTION_AGENCY_04_MEETING_ACCESS)}>
        <AgencyMeetingAccessSection control={control} readOnly={readOnly} />
      </Card>
    </>
  );
};

const PreActivationSectionCards = ({ control, errors, readOnly }) => {
  const { t } = useTranslation();
  const organizationType = useWatch({ control, name: "organizationType" });
  const isAgency = organizationType === PRE_ACTIVATION_ORGANIZATION_TYPE_AGENCY;

  return (
    <>
      <Card section={t(PRE_ACTIVATION_SECTION_CONTACT)}>
        <ContactSection
          control={control}
          errors={errors}
          readOnly={readOnly}
          isAgency={isAgency}
        />
      </Card>

      {isAgency ? (
        <AgencySectionCards
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      ) : (
        <BrandSectionCards
          control={control}
          errors={errors}
          readOnly={readOnly}
        />
      )}
    </>
  );
};

const PreActivationSubmitForm = ({
  onSubmit,
  submitError,
  isSaving,
  children,
}) => {
  const { t } = useTranslation();
  return (
    <Box component="form" onSubmit={onSubmit} sx={{ width: "100%" }} noValidate>
      {children}

      {submitError ? (
        <Typography
          role="alert"
          sx={{
            color: "error.main",
            fontSize: "14px",
            textAlign: "center",
            mb: 1,
          }}
        >
          {submitError}
        </Typography>
      ) : null}

      <Button
        type="submit"
        fullWidth
        color="primary"
        disabled={isSaving}
        sx={{
          mt: 1,
          py: 1.5,
          fontWeight: 600,
          borderRadius: "9px !important",
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          {isSaving ? (
            <CircularProgress size={22} sx={{ color: "inherit" }} />
          ) : null}
          <Box component="span">{t(PRE_ACTIVATION_SUBMIT_BUTTON)}</Box>
        </Box>
      </Button>

      <Typography
        component="p"
        variant="body2"
        sx={{
          mt: 2,
          color: "text.secondary",
          textAlign: "center",
        }}
      >
        {t(PRE_ACTIVATION_CONFIDENTIALITY_NOTE)}
      </Typography>
    </Box>
  );
};

const PreActivationLanguageSwitcher = ({ language, onChangeLanguage }) => (
  <Box sx={LANGUAGE_SWITCHER_STYLES}>
    <LanguageSwitcher
      language={language}
      onChangeLanguage={onChangeLanguage}
      colorButton="secondary"
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      sx={LANGUAGE_SWITCHER_BUTTON_STYLES}
      sxMenu={LANGUAGE_SWITCHER_MENU_STYLES}
    />
  </Box>
);

const PreActivationMainContent = ({
  ownerBackTo,
  readOnly,
  sectionCards,
  onSubmit,
  submitError,
  isSaving,
  control,
  onOrganizationTypeChange,
}) => (
  <PreActivationPageShell readOnly={readOnly}>
    {ownerBackTo ? <OwnerBackBar backTo={ownerBackTo} /> : null}
    {!readOnly ? <Navbar /> : null}
    {!readOnly ? (
      <Card>
        <HeroSection
          control={control}
          readOnly={readOnly}
          onOrganizationTypeChange={onOrganizationTypeChange}
        />
      </Card>
    ) : null}

    {readOnly ? (
      <Box sx={{ width: "100%" }}>{sectionCards}</Box>
    ) : (
      <PreActivationSubmitForm
        onSubmit={onSubmit}
        submitError={submitError}
        isSaving={isSaving}
      >
        {sectionCards}
      </PreActivationSubmitForm>
    )}
  </PreActivationPageShell>
);

const usePreActivationLanguage = ({ readOnly = false }) => {
  const navigate = useNavigate();
  const { lang } = useParams();
  const routeLanguage = getNormalizedLanguage(lang);
  const currentLanguage = readOnly
    ? getNormalizedLanguage(i18next.language) || "en"
    : routeLanguage;

  useEffect(() => {
    if (readOnly) {
      return;
    }
    if (!currentLanguage) {
      navigate(`/pre-activation/${getBrowserLanguage()}`, { replace: true });
      return;
    }

    if (i18next.language !== currentLanguage) {
      i18next.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, navigate, readOnly]);

  const onChangeLanguage = (language) => {
    const nextLanguage = getNormalizedLanguage(language);
    if (!nextLanguage || nextLanguage === currentLanguage) {
      return;
    }
    i18next.changeLanguage(nextLanguage);
    if (!readOnly) {
      navigate(`/pre-activation/${nextLanguage}`);
    }
  };

  return {
    currentLanguage,
    onChangeLanguage,
    shouldWaitForValidLanguage: !readOnly && !currentLanguage,
  };
};

const PreActivation = ({
  readOnly = false,
  prefilledData = null,
  ownerBackTo = null,
}) => {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const { currentLanguage, onChangeLanguage, shouldWaitForValidLanguage } =
    usePreActivationLanguage({ readOnly });

  const validationSchema = useMemo(() => createPreActivationSchema(t), [t]);

  const { control, handleSubmit, reset, getValues, formState } = useForm({
    resolver: readOnly ? undefined : zodResolver(validationSchema),
    criteriaMode: "all",
    defaultValues: defaultFormValues,
    mode: readOnly ? "onChange" : "onSubmit",
    reValidateMode: "onChange",
    shouldFocusError: !readOnly,
  });

  const { errors } = formState;

  useFocusErrorForm(formState);

  useEffect(() => {
    if (prefilledData) {
      reset(prefilledData);
    }
  }, [prefilledData, reset]);

  const onOrganizationTypeChange = ({ organizationType }) => {
    reset(
      buildFormValuesForOrganizationType({
        organizationType,
        currentValues: getValues(),
      })
    );
  };

  if (shouldWaitForValidLanguage) {
    return null;
  }

  const onSubmitValid = async (formValues) => {
    setSubmitError(null);
    setIsSaving(true);
    const payload = sanitizePreActivationSubmitValues(formValues);
    let data;
    try {
      data = await savePreActivationFormRequest(payload);
    } catch (err) {
      const message =
        err?.response?.data?.message ?? err?.message ?? t(ERROR_OCCURRED);
      setSubmitError(message);
      setIsSaving(false);
      return;
    }
    setIsSaving(false);

    if (data?.ok) {
      reset(defaultFormValues);
      await SimpleAlert({
        title: t(PRE_ACTIVATION_SUBMIT_SUCCESS_TITLE),
        text: t(PRE_ACTIVATION_SUBMIT_SUCCESS_MESSAGE),
        icon: "success",
        confirmButtonText: t(OK),
      });
      return;
    }
    setSubmitError(data?.message ?? t(ERROR_OCCURRED));
  };

  const onSubmitInvalid = () => {
    setSubmitError(null);
  };

  const sectionCards = (
    <PreActivationSectionCards
      control={control}
      errors={errors}
      readOnly={readOnly}
    />
  );

  return (
    <>
      {!readOnly ? (
        <PreActivationLanguageSwitcher
          language={currentLanguage}
          onChangeLanguage={onChangeLanguage}
        />
      ) : null}
      <PreActivationMainContent
        ownerBackTo={ownerBackTo}
        readOnly={readOnly}
        sectionCards={sectionCards}
        onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}
        submitError={submitError}
        isSaving={isSaving}
        control={control}
        onOrganizationTypeChange={onOrganizationTypeChange}
      />
    </>
  );
};

export default PreActivation;
